import { ChevronDown, ChevronLeft, ChevronRight } from '@untitledui/icons'
import { useEffect, useRef, useState } from 'react'
import { Button as AriaButton, ListBox, ListBoxItem, Popover } from 'react-aria-components'
import { Tooltip } from '@/components/base/tooltip/tooltip'
import { cx } from '@/utils/cx'
import { periodLabel, periodsBetween, periodStatus, shiftMonth } from '../period'
import { useShell } from '../store'

const STATUS_LABEL = { open: 'open', upcoming: 'upcoming', closed: 'closed · read-only' } as const

/**
 * PRD-018 §3.3, §3.9: `‹ Aug 2026 · open ▾ ›`, month granularity, 18 months back and 3 ahead. Same
 * position on every route; reduced emphasis when the page does not follow the period (applied to the
 * trigger buttons only, never to the open listbox — it inherited the opacity and rendered see-through).
 * The month list is a React Aria ListBox in a Popover (audit P2-3): focus moves into it on open, arrow
 * keys move, Enter picks, Esc closes. The arrows disable at the ends of the range instead of clamping.
 */
export function PeriodSelector({ follows }: { follows: boolean }) {
  const { period, setPeriod, canShiftPeriod, bootstrap } = useShell()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const status = periodStatus(period, bootstrap.period.currentOpen)
  const months = periodsBetween(bootstrap.period.range.from, bootstrap.period.range.to).reverse()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault()
        setOpen(v => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const arrow = (by: -1 | 1) => (
    <AriaButton
      aria-label={by < 0 ? 'Previous month' : 'Next month'}
      isDisabled={!canShiftPeriod(by)}
      onPress={() => setPeriod(shiftMonth(period, by))}
      className="flex size-7 items-center justify-center rounded-md text-quaternary hover:bg-primary_hover disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-2 focus-visible:outline-focus-ring"
      data-testid={by < 0 ? 'period-prev' : 'period-next'}
    >
      {by < 0 ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />}
    </AriaButton>
  )

  const trigger = (
    <AriaButton
      ref={triggerRef}
      aria-haspopup="listbox"
      aria-expanded={open}
      onPress={() => setOpen(v => !v)}
      className="flex h-7 items-center gap-2 rounded-md px-2 text-sm font-medium text-primary hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-focus-ring"
      data-testid="period-button"
    >
      <span>{periodLabel(period)}</span>
      <span className={cx('rounded-full px-2 py-0.5 text-xs font-medium', status === 'open' ? 'bg-success-secondary text-success-primary' : status === 'closed' ? 'bg-warning-secondary text-warning-primary' : 'bg-secondary text-tertiary')} data-testid="period-status">
        {bootstrap.me.role === 'tax' && status !== 'closed' ? 'not yet closed' : STATUS_LABEL[status]}
      </span>
      <ChevronDown className="size-4 text-quaternary" />
    </AriaButton>
  )

  return (
    <div className={cx('relative flex items-center', follows ? '' : '[&>button]:opacity-60')} data-testid="period-selector" data-follows={follows}>
      {arrow(-1)}
      {follows ? trigger : <Tooltip title="This page does not follow the period" placement="bottom">{trigger}</Tooltip>}
      {arrow(1)}
      <Popover triggerRef={triggerRef} isOpen={open} onOpenChange={setOpen} placement="bottom start" offset={4} className="z-30 max-h-80 w-64 overflow-y-auto rounded-lg border border-secondary bg-primary p-1 shadow-lg outline-none">
        <ListBox
          aria-label="Pick a month"
          selectionMode="single"
          disallowEmptySelection
          autoFocus
          selectedKeys={[period]}
          onSelectionChange={(keys) => {
            const next = [...keys][0]
            if (typeof next === 'string' && next !== period)
              setPeriod(next)
            setOpen(false)
          }}
          className="outline-none"
          data-testid="period-listbox"
        >
          {months.map(m => (
            <ListBoxItem key={m} id={m} textValue={periodLabel(m)} className={({ isSelected, isFocused }) => cx('flex cursor-default items-center justify-between rounded-md px-2 py-1.5 text-sm outline-none', isSelected ? 'bg-brand-primary text-brand-secondary' : 'text-secondary', isFocused && !isSelected && 'bg-primary_hover')}>
              <span>{periodLabel(m)}</span>
              <span className="text-xs text-quaternary">{STATUS_LABEL[periodStatus(m, bootstrap.period.currentOpen)]}</span>
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </div>
  )
}
